import { toggleTools } from '../constants/editor.constants'

export interface KendoEditor {
  wrapper: HTMLElement & { [key: number]: HTMLElement }
  body?: HTMLElement
  focus: () => void
}

declare global {
  interface Window {
    jQuery: (selector: unknown) => JQueryWrapper
  }
}

interface JQueryWrapper {
  find: (selector: string) => JQueryWrapper
  each: (callback: (this: HTMLElement) => void) => JQueryWrapper
  attr: (name: string, value?: string) => string | JQueryWrapper
  hasClass: (className: string) => boolean
  closest: (selector: string) => JQueryWrapper
  text: () => string
  val: () => string
  data: (key: string) => unknown
}

/**
 * Applies ARIA labels to toolbar buttons and dropdowns for accessibility
 * @param editor - Kendo editor instance
 */
export const applyToolbarAriaLabels = (editor: KendoEditor): void => {

  const $ = window.jQuery
  
  // Handle regular toolbar buttons
  const $toolbarButtons = $(editor.wrapper).find('.k-tool[title]')
  $toolbarButtons.each(function(this: HTMLElement) {
    const $btn = $(this)
    const title = $btn.attr('title') as string
    if (title) {
      $btn.attr('aria-label', title)

      // Check if this is a toggle button by matching the title
      const isToggle = toggleTools.some(t =>
        title.toLowerCase().includes(t.toLowerCase())
      )

      if (isToggle) {
        // Set aria-pressed based on whether button has selected state
        const isPressed = $btn.hasClass('k-state-selected') || $btn.hasClass('k-state-active')
        $btn.attr('aria-pressed', isPressed.toString())
      }
    }
  })

  // Handle dropdown tools (formatting, fontName, fontSize, etc.)
  const $dropdowns = $(editor.wrapper).find('.k-dropdown, .k-combobox')
  $dropdowns.each(function(this: HTMLElement) {
    const $dropdown = $(this)
    // Get the dropdown wrapper that contains the title
    const $wrapper = $dropdown.closest('.k-tool')
    const title = ($wrapper.attr('title') || $dropdown.attr('title') || '') as string
    
    // Get the currently displayed value
    const $valueElement = $dropdown.find('.k-input-value-text, .k-input, .k-select')
    const currentValue = $valueElement.text().trim() || $dropdown.find('input').val() || ''
    
    // Set aria-label with current selection
    if (title && currentValue) {
      $dropdown.attr('aria-label', `${title}: ${currentValue}`)
    } else if (title) {
      $dropdown.attr('aria-label', title)
    }
    
    // Ensure combobox role is set for proper screen reader announcement
    if (!$dropdown.attr('role')) {
      $dropdown.attr('role', 'combobox')
    }
    $dropdown.attr('aria-haspopup', 'listbox')
  })
}

/**
 * Focuses the editor when it's ready (body is available)
 * @param editor - Kendo editor instance
 */
export const focusEditorWhenReady = (editor: KendoEditor): void => {
  // If editor exists but body isn't ready, observe for DOM changes
  if (editor && editor.wrapper?.[0]) {
    const observer = new MutationObserver(() => {
      if (editor.body) {
        editor.focus()
        observer.disconnect()
      }
    })
    
    observer.observe(editor.wrapper[0], {
      childList: true,
      subtree: true,
    })
  }
}
